import React, { useState, useRef, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import {
  useIsMobile,
  Input,
  BaseButton,
  CustomDatePicker,
  VisaTypeList,
  useT,
} from '@repo/common';

import { ReactComponent as CalendarIcon } from '@repo/common/assets/icons/16px/icon_16px_calendar_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

const VisaInfoForm = ({ visa, setResumeData }) => {
  const isMobile = useIsMobile();
  const t = useT();
  const containerRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const isCurrentlyVisa = (visaType) =>
    typeof visaType === 'string' && visaType.startsWith('No');

  const [visaInfos, setVisaInfos] = useState([
    { id: uuidv4(), visaType: null, visaTypeText: null, expiryDate: null },
  ]);

  const [activeState, setActiveState] = useState({
    index: null,
    field: null,
  });

  const closeAll = useCallback(() => {
    setActiveState({ index: null, field: null });
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closeAll();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [closeAll]);

  const addVisaInfo = () => {
    closeAll();
    setVisaInfos((prev) => [
      ...prev,
      { id: uuidv4(), visaType: null, visaTypeText: null, expiryDate: null },
    ]);
  };

  const updateVisaInfo = (index, field, value) => {
    setVisaInfos((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const handleInputClick = (index, field) => {
    if (activeState.index === index && activeState.field === field) {
      closeAll();
    } else {
      setActiveState({ index, field });
    }
  };

  useEffect(() => {
    const validVisas = visaInfos
      .filter((v) => v.visaType !== null || v.expiryDate !== null)
      .map((v) => {
        let visaCode = null;
        let visaText = null;

        if (v.visaType) {
          const [code, ...textParts] = v.visaType.split(' ');
          visaCode = code;
          visaText = textParts.join(' ');
        }

        return {
          ...v,
          visaType: visaCode,
          visaTypeText: visaText,
        };
      });

    setResumeData((prev) => ({
      ...prev,
      visa: validVisas,
    }));
  }, [visaInfos, setResumeData]);

  useEffect(() => {
    if (!isInitialized && visa && visa.length > 0) {
      setVisaInfos(
        visa.map((v) => ({
          id: uuidv4(),
          visaType: v.visaType
            ? `${v.visaType} ${v.visaTypeText ?? ''}`.trim()
            : null,
          expiryDate: v.expiryDate ? new Date(v.expiryDate) : null,
        })),
      );
      setIsInitialized(true);
    }
  }, [visa, isInitialized]);

  return (
    <div
      ref={containerRef}
      className='min-w-[260px] tabletH:max-w-[976px] w-full'
    >
      {visaInfos.map((visa, index) => {
        const isVisaTypeActive =
          activeState.index === index && activeState.field === 'visaType';
        const isExpiryActive =
          activeState.index === index && activeState.field === 'expiryDate';
        const isExpiryDisabled = isCurrentlyVisa(visa.visaType);

        return (
          <div
            key={visa.id}
            className='grid grid-cols-1 tabletH:grid-cols-2 gap-3 tabletH:gap-4 pb-3 tabletH:pb-6 items-end'
          >
            {/* Visa Type */}
            <div className='relative min-w-[200px] max-w-[700px]'>
              <Input
                label={
                  isVisaTypeActive
                    ? t('Current Visa type', '현재 비자 종류')
                    : ''
                }
                placeholder={
                  isVisaTypeActive
                    ? t('Select your visa type', '비자 종류를 선택해 주세요')
                    : t('Current Visa type', '현재 비자 종류')
                }
                value={visa.visaType || ''}
                state={isVisaTypeActive ? 'active' : 'default'}
                readOnly
                onClick={(e) => {
                  e.stopPropagation();
                  handleInputClick(index, 'visaType');
                }}
                className='placeholder:opacity-0 cursor-pointer w-full'
                rightIcon={
                  isVisaTypeActive ? <ArrowTopIcon /> : <ArrowBottomIcon />
                }
                onRightIconClick={(e) => {
                  e.stopPropagation();
                  handleInputClick(index, 'visaType');
                }}
                showStatusIcon={false}
              />

              {isVisaTypeActive && (
                <div className='relative w-full mt-1.5 tabletH:absolute tabletH:top-[85px] tabletH:left-0 tabletH:mt-0 z-10'>
                  <VisaTypeList
                    selectedOption={visa.visaType}
                    onSelect={(value) => {
                      updateVisaInfo(index, 'visaType', value);
                      if (value.startsWith('Currently')) {
                        updateVisaInfo(index, 'expiryDate', null);
                      }
                      closeAll();
                    }}
                    isMyPagePersonalInfo={true}
                  />
                </div>
              )}
            </div>

            {/* Expiry Date */}
            <div className='relative min-w-[260px] max-w-[700px]'>
              <Input
                label={
                  isExpiryActive ? t('Visa expiry date', '비자 만료일') : ''
                }
                placeholder={
                  isExpiryActive
                    ? t('Select expiry date', '만료일을 선택해 주세요')
                    : t('Visa expiry date', '비자 만료일')
                }
                value={
                  visa.expiryDate ? format(visa.expiryDate, 'dd MMM yyyy') : ''
                }
                state={isExpiryActive ? 'active' : 'default'}
                readOnly
                onClick={(e) => {
                  e.stopPropagation();
                  handleInputClick(index, 'expiryDate');
                }}
                className='placeholder:opacity-0 w-full'
                rightIcon={<CalendarIcon />}
                onRightIconClick={(e) => {
                  e.stopPropagation();
                  handleInputClick(index, 'expiryDate');
                }}
                showStatusIcon={false}
                disabled={isExpiryDisabled}
              />

              {isExpiryActive && (
                <CustomDatePicker
                  isVisaExpiryDate
                  selectedDate={visa.expiryDate}
                  onChange={(date) => {
                    updateVisaInfo(index, 'expiryDate', date);
                    closeAll();
                  }}
                  $isMobile={isMobile}
                  onClose={closeAll}
                  isMyPagePersonalInfoVisa
                />
              )}
            </div>
          </div>
        );
      })}

      {/* Add Button */}
      <div className='min-w-[260px] max-w-[976px]'>
        <BaseButton
          type='secondary'
          size='l'
          icon={<PlusIcon />}
          width='100%'
          onClick={addVisaInfo}
        />
      </div>
    </div>
  );
};

export default VisaInfoForm;
